import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip
} from '@mui/material';
import {
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import { PageContainer } from '../design-system';
import { PageTitleContainer } from '../components/common';
import projectService from '../services/projectService';
import useNotification from '../hooks/useNotification';

/**
 * ArchivedProjects Page
 * Displays all archived projects in a table view with unarchive and delete actions
 */
const ArchivedProjects = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Refs and dynamic height for scrollable content area
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');

  useLayoutEffect(() => {
    const updateContentHeight = () => {
      const mainHeaderHeight = 73;
      const titleHeaderHeight = 80;
      const extraPadding = 69;
      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };
    updateContentHeight();
    window.addEventListener('resize', updateContentHeight);
    return () => window.removeEventListener('resize', updateContentHeight);
  }, []);

  // Fetch archived projects on mount
  useEffect(() => {
    fetchArchivedProjects();
  }, []);

  const fetchArchivedProjects = async () => {
    try {
      setLoading(true);
      const response = await projectService.getProjects({
        status: 'archived',
        limit: 100
      });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching archived projects:', error);
      showNotification({
        message: 'Failed to load archived projects',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle unarchive action
  const handleUnarchive = async (project) => {
    try {
      setActionLoading(true);
      await projectService.updateProject(project._id, { status: 'active' });

      // Remove from local state
      setProjects(prev => prev.filter(p => p._id !== project._id));

      // Invalidate projects cache so the main projects page shows updated data
      queryClient.invalidateQueries(['projects']);

      showNotification({
        message: `Project "${project.name}" has been unarchived successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error unarchiving project:', error);
      showNotification({
        message: `Failed to unarchive project: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  // Confirm and execute delete
  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    try {
      setActionLoading(true);
      await projectService.deleteProject(projectToDelete._id);

      // Remove from local state
      setProjects(prev => prev.filter(p => p._id !== projectToDelete._id));

      // Invalidate projects cache
      queryClient.invalidateQueries(['projects']);

      showNotification({
        message: `Project "${projectToDelete.name}" has been deleted successfully`,
        type: 'success'
      });

      handleDeleteDialogClose();
    } catch (error) {
      console.error('Error deleting project:', error);
      showNotification({
        message: `Failed to delete project: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get creator name from project
  const getCreatorName = (project) => {
    // First check the new createdByName field
    if (project.createdByName) return project.createdByName;

    // Fallback to createdBy object or string
    const createdBy = project.createdBy;
    if (!createdBy) return '-';
    if (typeof createdBy === 'string') return createdBy;
    return createdBy.name || createdBy.email || createdBy.id || '-';
  };

  return (
    <PageContainer
      fullWidth={true}
      sx={{
        px: 3,
        py: 3,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden'
      }}
    >
      {/* Page Title */}
      <PageTitleContainer
        title={
          <>
            <IconButton
              onClick={() => navigate('/projects')}
              sx={{
                color: '#475969',
                mr: 1,
                '&:hover': { backgroundColor: 'rgba(71, 89, 105, 0.08)' }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            Archived projects
          </>
        }
        titleProps={{
          sx: { display: 'flex', alignItems: 'center' }
        }}
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px'
        }}
      />

      {/* Table Container */}
      <Box
        sx={{
          flex: 1,
          bgcolor: 'white',
          borderRadius: '4px',
          boxShadow: '0px 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Paper
          elevation={0}
          ref={contentRef}
          sx={{
            borderRadius: '4px',
            p: 5,
            width: '100%',
            height: contentHeight,
            bgcolor: 'white',
            overflowX: 'hidden',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            backgroundImage: 'none',
            '&::before': {
              display: 'none'
            },
            '&::after': {
              display: 'none'
            }
          }}
        >


          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : projects.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, height: "100%" }}>
              <Typography variant="body1" color="text.secondary">
                No archived projects found
              </Typography>
            </Box>
          ) : (
            <Box sx={{ px: 5, py: 5, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <TableContainer sx={{
                maxHeight: 'calc(100vh - 250px)',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-track': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  marginTop: '5px',
                  marginBottom: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.4)',
                  },
                },
                '&::-webkit-scrollbar-corner': {
                  backgroundColor: 'transparent',
                },
              }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '20%', }}>Name</TableCell>
                      <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '10%', }}>Short code</TableCell>
                      <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '10%', }}>Status</TableCell>
                      <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '16%', }}>Created by</TableCell>
                      <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '16%', }}>Created at</TableCell>
                      <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", width: '16%', }}>Updated at</TableCell>
                      <TableCell sx={{ fontStyle: "normal !important", fontSize: "16px !important", fontWeight: "bold !important", }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow
                        key={project._id}
                        sx={{
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                          '&:nth-of-type(even)': {
                            backgroundColor: 'white !important'
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {project.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {project.shortCode || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={project.status}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: '#ffebee',
                              color: '#c62828',
                              textTransform: 'capitalize',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getCreatorName(project)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(project.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(project.updatedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{
                              width: 32,
                              height: 32,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '4px',
                              marginRight: '10px'
                            }}>
                              <Tooltip title="Unarchive" placement="left">
                                <IconButton
                                  size="small"
                                  onClick={() => handleUnarchive(project)}
                                  disabled={actionLoading}
                                  sx={{
                                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                                  }}
                                >
                                  <UnarchiveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Box sx={{
                              width: 32,
                              height: 32,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '4px',
                            }}>
                              <Tooltip title="Delete" placement="left">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(project)}
                                  disabled={actionLoading}
                                  sx={{
                                    color: 'error.main',
                                    '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.04)' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the project "{projectToDelete?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDeleteDialogClose}
            disabled={actionLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={actionLoading}
            variant="contained"
            color="error"
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default ArchivedProjects;
